"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const mobx_1 = require("mobx");
const ecommerce_1 = require("../../api/ecommerce");
const string_1 = require("../../common/js/helper/string");
const AuthorizationStore_1 = __importDefault(require("../../common/js/stores/AuthorizationStore"));
const configuration_1 = __importDefault(require("../apis/configuration/shop/configuration"));
const ProductType_1 = __importDefault(require("../enums/ProductType"));
const compare_1 = require("../helper/compare");
class LicenseStore {
    api = new ecommerce_1.MassiveLicenseApi(configuration_1.default);
    internalMassiveLicenses = new Map();
    loading = false;
    downloader = null;
    internalDeactivateEllipsis = [];
    internalTriggerable = false;
    storedForCustomerId = null;
    constructor() {
        (0, mobx_1.makeObservable)(this, {
            internalMassiveLicenses: mobx_1.observable,
            loading: mobx_1.observable,
            downloader: mobx_1.observable,
            internalDeactivateEllipsis: mobx_1.observable,
            internalTriggerable: mobx_1.observable,
            triggerable: mobx_1.computed,
            isLoading: mobx_1.computed,
            isDownloading: mobx_1.computed,
            licenses: mobx_1.computed,
            rootTableLicenses: mobx_1.computed,
            baseLicenses: mobx_1.computed,
            deactivateEllipsis: mobx_1.computed,
            setDeactivateEllipsis: mobx_1.action.bound,
            setTmpActivated: mobx_1.action.bound,
            clearDeactivateEllipsis: mobx_1.action.bound,
            readMassiveLicenses: mobx_1.action.bound,
            triggerObservables: mobx_1.action.bound,
        });
    }
    get isLoading() {
        return this.loading;
    }
    get isDownloading() {
        return this.downloader !== null;
    }
    get licenses() {
        return this.internalMassiveLicenses;
    }
    get rootTableLicenses() {
        return [...this.licenses.values()]
            .filter((license) => {
            switch (license.type) {
                case ecommerce_1.MassiveLicenseTypeEnum.Base:
                    return true;
                case ecommerce_1.MassiveLicenseTypeEnum.Option:
                    return (!license.activated &&
                        (0, string_1.slugifyKey)(license.productType) === ProductType_1.default.ZUND_CUT_CENTER &&
                        this.baseLicenses.every(({ externalId }) => externalId !== license.externalId));
                default:
                    return false;
            }
        })
            .sort((a, b) => (0, compare_1.naturalCompare)(a.name ?? '', b.name ?? ''));
    }
    get baseLicenses() {
        return [...this.licenses.values()].filter((license) => license.type === ecommerce_1.MassiveLicenseTypeEnum.Base);
    }
    get deactivateEllipsis() {
        return this.internalDeactivateEllipsis;
    }
    setDeactivateEllipsis(licenseId) {
        this.internalDeactivateEllipsis.push(licenseId);
    }
    clearDeactivateEllipsis() {
        this.internalDeactivateEllipsis = [];
    }
    get triggerable() {
        return this.internalTriggerable;
    }
    triggerObservables() {
        this.internalTriggerable = !this.internalTriggerable;
    }
    getLicenseOfTypeForExternalId(externalId, type) {
        return Array.from(this.internalMassiveLicenses.values()).filter((license) => license.type === type && license.externalId === externalId);
    }
    getLicenseOfTypeForContainerNumber(containerNumber, type, externalIdToFilterOutBy) {
        return Array.from(this.internalMassiveLicenses.values()).filter((license) => license.type === type &&
            license.containerNumber === containerNumber &&
            license.externalId !== externalIdToFilterOutBy &&
            // Only licences that don't already have a potential parent.
            this.baseLicenses.every(({ externalId }) => externalId !== license.externalId));
    }
    readMassiveLicenses() {
        if (this.loading && AuthorizationStore_1.default.customerId === this.storedForCustomerId) {
            return Promise.resolve(this.internalMassiveLicenses);
        }
        this.storedForCustomerId = AuthorizationStore_1.default.customerId;
        this.loading = true;
        return this.api
            .readMassiveLicenseList({
            associations: {
                massiveMachine: { ' ': 1 },
                media: { ' ': 1 },
            },
        })
            .then((0, mobx_1.action)((massiveLicenses) => {
            if (AuthorizationStore_1.default.customerId !== this.storedForCustomerId) {
                return Promise.reject();
            }
            this.internalMassiveLicenses.clear();
            massiveLicenses.map((massiveLicense) => {
                this.internalMassiveLicenses.set(massiveLicense.id, massiveLicense);
            });
            return this.internalMassiveLicenses;
        }))
            .finally((0, mobx_1.action)(() => {
            if (AuthorizationStore_1.default.customerId !== this.storedForCustomerId) {
                return;
            }
            this.loading = false;
        }));
    }
    downloadMassiveLicense(licenseId) {
        if (this.downloader != null) {
            return this.downloader;
        }
        return (this.downloader = this.api
            .downloadMassiveLicenseRaw({ id: licenseId })
            .then(async (apiResponse) => {
            const [, filename] = (apiResponse.raw.headers.get('content-disposition') ?? '').split('filename=');
            return {
                fileName: filename ?? 'Zund-Licenses.zip',
                blob: await apiResponse.value(),
            };
        })
            .finally(() => {
            this.downloader = null;
        }));
    }
    consentLicense(licenseId) {
        return this.api.consentLicense({ id: licenseId });
    }
    updateLicenseUsage(id, usage) {
        return this.api.updateLicense({ id, massiveLicensePatchRequestBody: { usage } });
    }
    updateLicenseContactId(id, contactId) {
        return this.api.updateLicense({ id, massiveLicensePatchRequestBody: { contactId } });
    }
    setTmpActivated(license) {
        license.activated = true;
    }
}
exports.default = new LicenseStore();
